import type { DashboardByMaterialPerDay, RegistryMaterial } from '@/integrations/agreements/client';
import { fmtWeight } from './formatWeight';

/**
 * Unidade em que a quantidade realizada de uma coleta é medida — a `unity` do cadastro do material.
 *
 * O agreements devolve o realizado (`weight`) **já na unidade do material**: litros para óleo e os
 * demais líquidos, unidades para lâmpada, kg para o resto. A tela não converte nada; só rotula. Antes
 * ela chamava tudo de "kg" e ainda mostrava `volume = weight × 1,1`, o que convertia o óleo duas vezes.
 */
export type MaterialUnit = 'kg' | 'Lt' | 'un';

/** `lt` → "Lt", `un` → "un"; qualquer outra coisa (inclusive sem cadastro) é kg. */
export function materialUnitLabel(unity?: string | null): MaterialUnit {
  const normalized = unity?.trim().toLowerCase();
  if (normalized === 'lt') return 'Lt';
  if (normalized === 'un') return 'un';
  return 'kg';
}

/** Id do material → unidade, a partir do catálogo (`GET /api/v1/registry/material`). */
export function buildMaterialUnitMap(materials?: RegistryMaterial[] | null): Map<number, MaterialUnit> {
  const units = new Map<number, MaterialUnit>();
  for (const material of materials ?? []) units.set(material.id, materialUnitLabel(material.unity));
  return units;
}

/** Unidade de um material; fora do catálogo (ou catálogo indisponível) cai em kg. */
export function unitForMaterial(
  materialId: number | null | undefined,
  unitById: ReadonlyMap<number, MaterialUnit>,
): MaterialUnit {
  if (materialId == null) return 'kg';
  return unitById.get(materialId) ?? 'kg';
}

/**
 * Quantidade realizada com a unidade, ex.: "720 kg", "40 Lt", "12 un". Mesmo truncamento do peso
 * (GR-548: nunca arredonda para cima). Zero ou vazio → `null`: quem exibe decide o traço.
 */
export function formatQuantity(quantity: number | null | undefined, unit: MaterialUnit): string | null {
  if (quantity == null || quantity <= 0) return null;
  return `${fmtWeight(quantity).value} ${unit}`;
}

export interface TotalsByUnit {
  /** Soma do realizado dos materiais medidos em kg. */
  weightKg: number;
  /** Coletas dos materiais em kg — divisor de "kg por coleta". */
  kgCollects: number;
  /** Soma do realizado dos materiais medidos em litros (sem conversão). */
  volumeLt: number;
  /** Coletas dos materiais em litros — divisor de "Lt por coleta". */
  ltCollects: number;
}

/**
 * Separa os totais do card "Peso total coletado" por unidade, a partir das linhas por material do
 * Dashboard. O total pronto do agreements (`total_weight`) soma litros de óleo como kg, e o
 * `total_volume` é esse mesmo total × 1,1 — por isso o card não usa nenhum dos dois.
 *
 * Material em `un` não entra em peso nem em volume: o card já mostra os itens à parte.
 */
export function splitTotalsByUnit(
  rows: readonly Pick<DashboardByMaterialPerDay, 'material_id' | 'total_weight' | 'total_collects'>[],
  unitById: ReadonlyMap<number, MaterialUnit>,
): TotalsByUnit {
  const totals: TotalsByUnit = { weightKg: 0, kgCollects: 0, volumeLt: 0, ltCollects: 0 };
  for (const row of rows) {
    const unit = unitForMaterial(row.material_id, unitById);
    if (unit === 'kg') {
      totals.weightKg += row.total_weight ?? 0;
      totals.kgCollects += row.total_collects ?? 0;
    } else if (unit === 'Lt') {
      totals.volumeLt += row.total_weight ?? 0;
      totals.ltCollects += row.total_collects ?? 0;
    }
  }
  return totals;
}
